import {
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { DataWarehouseCopySink } from 'app/model/pipeline/copysink/data-warehouse.copysink';
import { DataWarehouseCopySource } from 'app/model/pipeline/copysource/data-warehouse.copysource';
import {
    CredentialTypeOptions,
    Metamodel,
    ModelConstants,
    shellStringResources,
    warehouseAuthTypes
} from 'shell-models-lib';
import { azureSqlDWMetadata } from './azure-sql-dw-metadata';
import { format } from 'app/shared/utils/string.util';
import { authReferenceSetting } from './lakehouse-metadata';
import { IDataflowBuildInTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { GlobalVariables } from 'app/services/globals';

const warehouseAuthOptions: Array<{ label: string; value: string }> = [
    {
        label: stringResources.AuthenticationType_ServicePrincipal,
        value: warehouseAuthTypes.ServicePrincipalAuthentication
    },
    {
        label: stringResources.AuthenticationType_ManagedServiceIdentity,
        value: warehouseAuthTypes.MSIAuthentication
    },
    {
        label: stringResources.AuthenticationType_UserAssignedManagedIdentity,
        value: warehouseAuthTypes.UserAssignedManagedIdentity
    }
];

const dataflowWarehouse: IDataflowBuildInTypeMetadata = {
    type: DatasetFormatType.Warehouse,
    settings: adfModelTypeMap.Warehouse.dataflowSettings,
    useInlineProjection: true,
    enableImportSchemaInSink: true,
    enableAlterRow: true
};

export const dataWarehouseMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const sqlDWMetadata = await azureSqlDWMetadata(injector);
    const metadata: ILinkedServiceTypeMetadata = {
        ...sqlDWMetadata,
        copyJob: { ...sqlDWMetadata.copyJob, omitConnectionDatabaseFromDataset: false },
        icon: {
            className: 'svg-i_dataWarehouse'
        },
        type: (__IS_FABRIC_DPE__ || __IS_FABRIC_MDF__) ? ModelConstants.dataWarehouseLinkedService : ModelConstants.warehouseLinkedService,
        displayName: __IS_FABRIC_DPE__
            ? shellStringResources.Warehouse_DisplayText
            : shellStringResources.Warehouse_Fabric_DisplayText,
        dmtsCredentialConnectionType: adfModelTypeMap.warehouseCredential.linkedServiceType,

        linkedServiceNamePrefix: 'Warehouse',
        category: __IS_FABRIC_DPE__ ? ['Workspace'] : ['Azure'],
        copySinkTypes: [Metamodel.modelFor(DataWarehouseCopySink).name],
        copySourceTypes: [Metamodel.modelFor(DataWarehouseCopySource).name],

        supportDataFlowSource: true,
        supportDataFlowSink: true,
        dataflowSource: [dataflowWarehouse],
        dataflowSink: [dataflowWarehouse],

        supportCopyJobSource: true,
        supportCopyJobSink: true,
        supportMultipleIncrementalColumns: true,

        supportedInTrident: true,
        fabricArtifactType: ModelConstants.dataWarehouseArtifactType,
        pqoDataSourceOption: undefined,
        supportedAuthKindsInTrident: undefined,
        notSupportDeleteActivity: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        supportFirstClassParameterExperience: !__IS_FABRIC_DPE__,
        controls: __IS_FABRIC_DPE__
            ? []
            : [
                  {
                      type: 'radio-switch',
                      nested: 1,
                      label: format(
                          stringResources.FabricArtifactSelectionMethod,
                          stringResources.DataWarehouse_DisplayText
                      ),
                      options: [
                          {
                              value: 'selection',
                              label: stringResources.FromSelectionLabel
                          },
                          {
                              value: 'manual',
                              label: stringResources.EnterManuallyLabel
                          }
                      ],
                      isLabelVertical: true,
                      defaultValue: '{workspaceId != null || parameterized ? "manual" : "selection"}',
                      cases: [
                          {
                              caseValue: 'selection',
                              children: [
                                  {
                                      type: 'fabric-artifact-account-select',
                                      bind: {
                                          workspaceID: 'workspaceId',
                                          artifactID: 'artifactId',
                                          endpoint: 'endpoint'
                                      },
                                      artifactType: 'Warehouse',
                                      artifactTypePlural: 'warehouses',
                                      artifactIdLabel: stringResources.Warehouse_Name_DisplayText,
                                      showEndpoint: true,
                                      required: true,
                                      validators: [{ type: 'required', errorMessage: '' }],
                                      isLabelVertical: true
                                  }
                              ]
                          },
                          {
                              caseValue: 'manual',
                              children: [
                                  {
                                      type: 'fabric-artifact-account-edit',
                                      bind: {
                                          workspaceId: 'workspaceId',
                                          artifactId: 'artifactId',
                                          endpoint: 'endpoint'
                                      },
                                      artifactType: 'Warehouse',
                                      artifactTypePlural: 'warehouses',
                                      showEndpoint: true,
                                      required: true,
                                      validators: [{ type: 'required', errorMessage: '' }],
                                      isLabelVertical: true
                                  }
                              ]
                          }
                      ]
                  },
                  {
                      type: 'select-switch',
                      bind: 'authenticationMethod',
                      label: stringResources.ModelName_AuthenticationType,
                      options: warehouseAuthOptions,
                      isLabelVertical: true,
                      defaultValue: warehouseAuthTypes.ServicePrincipalAuthentication,
                      cases: [
                          {
                              caseValue: warehouseAuthTypes.ServicePrincipalAuthentication,
                              children: [authReferenceSetting(CredentialTypeOptions.ServicePrincipal)]
                          },
                          {
                              caseValue: warehouseAuthTypes.MSIAuthentication,
                              children: !GlobalVariables.isSamiSupportEnabled
                                  ? [
                                        {
                                            type: 'management-identity-info',
                                            resourceType: stringResources.Lakehouse_DisplayText,
                                            link: 'https://learn.microsoft.com/azure/data-factory/connector-microsoft-fabric-warehouse?tabs=data-factory#service-principal-authentication'
                                        }
                                    ]
                                  : [authReferenceSetting(CredentialTypeOptions.ManagedIdentity)]
                          },
                          {
                              // for UAMI there won't be any radio buttons, since inline mode is not supported.
                              caseValue: warehouseAuthTypes.UserAssignedManagedIdentity,
                              children: [
                                  {
                                      type: 'credential-select',
                                      bind: 'credential',
                                      filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                                      required: true,
                                      validators: [{ type: 'required', errorMessage: '' }]
                                  }
                              ]
                          }
                      ]
                  }
              ],
        additionalPropertiesInFabric: undefined
    };
    return metadata;
};
